import type { LucideIcon } from 'lucide-react';
import { Ban } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { Skeleton } from '@/shared/components/ui/skeleton';

export type FeatureGateStatus = 'loading' | 'unavailable' | 'error';

interface FeatureGateCopy {
  title: string;
  description: string;
}

interface FeatureGateProps {
  status: FeatureGateStatus;
  /** Icon for the unavailable/error states. Defaults to a generic "blocked" icon. */
  icon?: LucideIcon;
  /** Extra skeleton rows shown under the title row while `status === 'loading'`. */
  loadingRows?: number;
  /** Copy for a `404`-style "not entitled" outcome — no retry, nothing the user can do. */
  unavailable: FeatureGateCopy;
  /** Copy for any other failure (network, 5xx) — the probe itself broke. */
  error: FeatureGateCopy;
  testIds: {
    loading: string;
    unavailable: string;
    error: string;
  };
  className?: string;
}

/**
 * Shared shell for "this add-on isn't available for the tenant yet" —
 * every backend feature flag we've seen 404s the *entire* module when the
 * tenant isn't entitled, not just a single endpoint. Rendering each
 * feature's own screen anyway means every widget on it fires its own failed
 * request and its own error toast independently, which is the actual bug
 * this exists to prevent.
 *
 * A feature doesn't use this directly — it writes a small wrapper component
 * (see `time-off/components/activation/TimeOffActivationGate.tsx`) that
 * knows how to probe *its own* backend feature flag, reduces the result to
 * a `FeatureGateStatus`, and renders this shell for every state except
 * "ready" (where it renders its children instead). That wrapper is the
 * reusable per-feature seam — this component is only the presentation.
 *
 * @example
 * function MyFeatureGate({ children }: { children: ReactNode }) {
 *   const probe = useMyFeatureProbe();
 *   if (probe.isLoading) {
 *     return <FeatureGate status="loading" testIds={ids} unavailable={u} error={e} />;
 *   }
 *   if (probe.isError) {
 *     const status = isNotFoundError(probe.error) ? 'unavailable' : 'error';
 *     return <FeatureGate status={status} testIds={ids} unavailable={u} error={e} />;
 *   }
 *   return <>{children}</>;
 * }
 */
export function FeatureGate({
  status,
  icon: Icon = Ban,
  loadingRows = 1,
  unavailable,
  error,
  testIds,
  className,
}: FeatureGateProps) {
  if (status === 'loading') {
    return (
      <div className={cn('space-y-4', className)} data-testid={testIds.loading}>
        <Skeleton className="h-8 w-48" />
        {Array.from({ length: loadingRows }).map((_, i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  const copy = status === 'unavailable' ? unavailable : error;
  const testId = status === 'unavailable' ? testIds.unavailable : testIds.error;

  return (
    <div
      className={cn(
        'flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-12 px-6 text-center',
        className,
      )}
      data-testid={testId}
    >
      <Icon className="size-10 text-muted-foreground" aria-hidden="true" />
      <p className="text-sm font-medium text-foreground">{copy.title}</p>
      <p className="max-w-md text-sm text-muted-foreground">{copy.description}</p>
    </div>
  );
}
